export const ROSTER_PAGES = [
    { id: "task-operations", name: "Task Operations" },
    { id: "calendar", name: "Calendar" },
    { id: "delivery-timeline", name: "Delivery Timeline" },
    { id: "team-load", name: "Team Load" },
    { id: "partner-load", name: "Partner Load" }
];
export class RosterConfigError extends Error {
    constructor(message) {
        super(message);
        this.name = "RosterConfigError";
    }
}
const NAME_RE = /^[\p{L}][\p{L}\p{M}'’.\- ]{0,79}$/u;
export function isRosterPageId(id) {
    return ROSTER_PAGES.some((page) => page.id === id);
}
export function personDisplayName(person) {
    return `${person.firstName} ${person.lastName}`.replace(/\s+/g, " ").trim();
}
export function normalizePerson(person) {
    return {
        firstName: person.firstName.trim().replace(/\s+/g, " "),
        lastName: person.lastName.trim().replace(/\s+/g, " ")
    };
}
/** Page override when one is set, otherwise the default list. Unknown pages use the default. */
export function resolvePagePeople(config, pageId) {
    if (isRosterPageId(pageId)) {
        const page = config.pages[pageId];
        if (page != null)
            return page;
    }
    return config.default;
}
export function resolvedAssigneeNames(config, pageId) {
    return resolvePagePeople(config, pageId).map(personDisplayName).filter((name) => name.length > 0);
}
export function rosterView(config) {
    const resolved = {};
    for (const page of ROSTER_PAGES)
        resolved[page.id] = resolvedAssigneeNames(config, page.id);
    return { config, resolved };
}
export function normalizeRoster(config) {
    const pages = {};
    for (const page of ROSTER_PAGES) {
        const list = config.pages[page.id];
        pages[page.id] = list == null ? null : list.map(normalizePerson);
    }
    return { default: config.default.map(normalizePerson), pages };
}
/** Human-readable problem, or null when the config is safe to store and query with. */
export function rosterConfigError(config) {
    const defaultError = listError(config.default, "Default list");
    if (defaultError)
        return defaultError;
    if (config.default.length === 0)
        return "The default list needs at least one person.";
    for (const page of ROSTER_PAGES) {
        if (!(page.id in config.pages))
            return `${page.name} is missing from the roster.`;
        const list = config.pages[page.id];
        if (list == null)
            continue;
        const error = listError(list, page.name);
        if (error)
            return error;
    }
    return null;
}
export function coerceRoster(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) {
        throw new RosterConfigError("Roster config must be an object.");
    }
    const raw = input;
    if (!Array.isArray(raw.default))
        throw new RosterConfigError("The default list is missing.");
    if (raw.pages != null && (typeof raw.pages !== "object" || Array.isArray(raw.pages))) {
        throw new RosterConfigError("pages must be an object.");
    }
    const pagesInput = (raw.pages ?? {});
    const pages = {};
    for (const page of ROSTER_PAGES) {
        const value = pagesInput[page.id];
        if (value == null) {
            pages[page.id] = null;
            continue;
        }
        if (!Array.isArray(value)) {
            throw new RosterConfigError(`${page.name} must be a list of people or null to use the default list.`);
        }
        pages[page.id] = value.map((person, index) => coercePerson(person, `${page.name} row ${index + 1}`));
    }
    const config = normalizeRoster({
        default: raw.default.map((person, index) => coercePerson(person, `Default list row ${index + 1}`)),
        pages
    });
    const error = rosterConfigError(config);
    if (error)
        throw new RosterConfigError(error);
    return config;
}
function listError(people, label) {
    const seen = new Set();
    for (const person of people) {
        const name = personDisplayName(person);
        if (!person.firstName || !person.lastName)
            return `${label}: first and last name are both required.`;
        if (!NAME_RE.test(person.firstName) || !NAME_RE.test(person.lastName)) {
            return `${label}: "${name}" can only use letters, spaces, hyphens, apostrophes, and periods.`;
        }
        const key = name.toLocaleLowerCase();
        if (seen.has(key))
            return `${label}: "${name}" is listed more than once.`;
        seen.add(key);
    }
    return null;
}
function coercePerson(value, label) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new RosterConfigError(`${label} must be a person with a first and last name.`);
    }
    const row = value;
    if (typeof row.firstName !== "string" || typeof row.lastName !== "string") {
        throw new RosterConfigError(`${label} must include firstName and lastName.`);
    }
    return { firstName: row.firstName, lastName: row.lastName };
}
//# sourceMappingURL=roster.js.map